import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/cn';
import { Icon } from '../primitives/Icon';

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  subtitle?: ReactNode;
  /** Pixel width of the drawer panel. Defaults to 480 per JSX prototype. */
  width?: number;
  /** Footer slot. Typically a row of action buttons. */
  actions?: ReactNode;
  children?: ReactNode;
  className?: string;
  /** Side the drawer slides in from. Defaults to right. */
  side?: 'right' | 'left';
}

/**
 * Atlas-styled drawer — right-side (default) slide-over with title /
 * subtitle / body / footer slots. Imperative `open` / `onClose` API
 * matching the JSX prototype. ESC closes; backdrop click closes.
 *
 * For full Radix Sheet semantics (controlled state, focus trap,
 * scroll-lock guarantees) use `Sheet` directly. This component
 * targets the common slot-pattern Atlas pages adopt for module
 * detail sheets and edit panels.
 */
export function Drawer({
  open,
  onClose,
  title,
  subtitle,
  width = 480,
  actions,
  children,
  className,
  side = 'right',
}: DrawerProps) {
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const sideClass =
    side === 'right'
      ? 'right-0 border-l data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right'
      : 'left-0 border-r data-[state=open]:slide-in-from-left data-[state=closed]:slide-out-to-left';

  // Portal to <body>: `position: fixed` is relative to the nearest ancestor with a transform /
  // filter / contain, so inside the app's scrolling shell the overlay stopped at one screen height
  // and the panel scrolled away with the page (Hassan, /k/playbook, 2026-10-01).
  const node = (
    <>
      <div
        role="presentation"
        onClick={onClose}
        data-state={open ? 'open' : 'closed'}
        aria-hidden={!open}
        className={cn(
          'fixed inset-0 z-40 bg-black/40 transition-opacity dark:bg-black/60',
          open ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0',
        )}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        data-state={open ? 'open' : 'closed'}
        style={{ width }}
        className={cn(
          'fixed inset-y-0 z-50 flex max-w-full flex-col border-border bg-bg-surface shadow-xl',
          'transition-transform duration-hover ease-out',
          sideClass,
          open ? 'translate-x-0' : side === 'right' ? 'translate-x-full' : '-translate-x-full',
          className,
        )}
      >
        <div className="flex items-start justify-between gap-4 border-b border-border px-6 py-4">
          <div className="flex flex-col gap-1">
            {title ? <div className="text-h3 text-text-primary">{title}</div> : null}
            {subtitle ? (
              <div className="text-body text-text-secondary">{subtitle}</div>
            ) : null}
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className={cn(
              'inline-flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center rounded-md text-text-secondary',
              'hover:bg-bg-elevated hover:text-text-primary',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500',
              'transition-colors',
            )}
          >
            <Icon name="x" size={16} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
        {actions ? (
          <div className="flex items-center justify-end gap-3 border-t border-border px-6 py-4">
            {actions}
          </div>
        ) : null}
      </aside>
    </>
  );
  return typeof document === 'undefined' ? node : createPortal(node, document.body);
}
